import React, { useState, useEffect, useCallback } from "react";
import { Container, Box, CircularProgress, Alert, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import UserTable from "../components/auth/user-table";
import UserFormDialog from "../components/auth/user-form-dialog";
import ConfirmDialog from "../components/common/confirm-dialog";

import { useSnackbar } from "../contexts/snackbar-context";
import { useLoading } from "../contexts/loading-context";
import { useHasPermission } from "../hooks/useHasPermission";
import {
  setUserRole,
} from "../services/resolveCurrentUserRole";
import { AppRole } from "../types/roles";
import { User, UpdateUserRequest } from "../types/user";
import { useAccessManagementService } from "../services/accessManagementService";
import { useTenant } from "../contexts/tenant-context";

const UserAccess: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const { getAllUsers, getTenantUsers, deleteUser, updateUser, syncAzureUsers } = useAccessManagementService();
  const { isGlobalAdmin, selectedTenantId, userSession } = useTenant();
  const isActingAsGlobalAdmin = isGlobalAdmin && selectedTenantId === '__GLOBAL_ADMIN__';

  const canReadUsers = useHasPermission("accessmanagement", "read");
  const canCreateUser = useHasPermission("accessmanagement", "create");

  const [users, setUsers] = useState<User[]>([]);
  const [availableUsersToAdd, setAvailableUsersToAdd] = useState<
    Array<{ id: string; entraObjectId: string; email: string; displayName: string; isEnabled: boolean }>
  >([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit">("edit");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userPendingDelete, setUserPendingDelete] = useState<User | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [isSyncingUsers, setIsSyncingUsers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isInitialLoad = loading && users.length === 0;

  const loadAvailableUsers = useCallback(async () => {
    try {
      const appUsers = await getAllUsers();
      const mapped = appUsers
        .map((u) => {
          const id = u.id?.trim() ?? "";
          const entraObjectId = u.entraObjectId?.trim() ?? "";
          const email = u.email?.trim() ?? "";
          const displayName = u.displayName?.trim() ?? "";

          if (!id || !email) return null;
          return { id, entraObjectId, email, displayName, isEnabled: u.isEnabled };
        })
        .filter(
          (u): u is { id: string; entraObjectId: string; email: string; displayName: string; isEnabled: boolean } =>
            u !== null
        );

      setAvailableUsersToAdd(mapped);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load available users";
      showError(errorMessage);
      setAvailableUsersToAdd([]);
    }
  }, [getAllUsers, showError]);
  
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const apiUsers = isActingAsGlobalAdmin
        ? await getAllUsers()
        : selectedTenantId
          ? await getTenantUsers(selectedTenantId)
          : await getAllUsers();

      const mapped: User[] = apiUsers.map((u) => ({
        id:  u.id,
        entraObjectId: u.entraObjectId || u.id,
        email: u.email,
        displayName: u.displayName,
        status: u.isEnabled ? "enabled" : "disabled",
        startDateUtc: u.startDateUtc,
        endDateUtc: u.endDateUtc,
        isGlobalAdmin: u.isGlobalAdmin ?? false,
        tenants: (u.tenantRoleMappings ?? []).map((m) => ({
          id: m.id,
          tenantId: m.tenantId,
          tenantName: m.tenantName ?? "",
          role: m.role,
        })),
      }));

      setUsers(mapped);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load users";
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getAllUsers, getTenantUsers, isActingAsGlobalAdmin, selectedTenantId, showError]);

  useEffect(() => {
    void loadUsers();
  }, [selectedTenantId, loadUsers]);

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setDialogMode("edit");
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setDialogMode("add");
    setIsDialogOpen(true);
    void loadAvailableUsers();
  };

  const handleSyncUsers = async () => {
    if (isSyncingUsers) return;

    try {
      setIsSyncingUsers(true);
      showLoading("Syncing users...");

      const result = await syncAzureUsers();

      showSuccess(
        `Sync completed. Inserted: ${result.insertedCount}, Updated: ${result.updatedCount}, Skipped: ${result.skippedCount}`
      );

      await loadUsers();
      await loadAvailableUsers();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to sync users";
      showError(errorMessage);
    } finally {
      hideLoading();
      setIsSyncingUsers(false);
    }
  };

  const handleDelete = (user: User) => {
    setUserPendingDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    if (isDeletingUser) return;
    setIsDeleteDialogOpen(false);
    setUserPendingDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!userPendingDelete) return;
    try {
      setIsDeletingUser(true);

      const ok = await deleteUser(userPendingDelete.id);
      if (!ok) {
        showError("Failed to delete user");
        return;
      }

      showSuccess("User deleted successfully");
      await loadUsers();
      await loadAvailableUsers();

      setIsDeleteDialogOpen(false);
      setUserPendingDelete(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to delete user";
      showError(errorMessage);
    } finally {
      setIsDeletingUser(false);
    }
  };

  const handleSave = async (userId: string, updates: UpdateUserRequest) => {
    try {
      if (
        updates.entraObjectId &&
        updates.email &&
        updates.displayName &&
        typeof updates.isEnabled === "boolean" &&
        Array.isArray(updates.tenantRoleMappings)
      ) {
        const ok = await updateUser({
          entraObjectId: updates.entraObjectId,
          email: updates.email,
          displayName: updates.displayName,
          isEnabled: updates.isEnabled,
          startDateUtc: updates.startDateUtc,
          endDateUtc: updates.endDateUtc,
          tenantRoleMappings: updates.tenantRoleMappings,
        });

        if (!ok) {
          showError("Failed to update user");
          return;
        }
      }

      // Optimistic feedback, but always refresh from DB to keep grid consistent
      showSuccess("User saved successfully.");
      await loadUsers();

      if (updates.displayName) {
        setUserRole(userId, updates.displayName as AppRole);
      }

      // Refresh add-dialog dropdown list as well (in case user became enabled)
      await loadAvailableUsers();

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to refresh users";
      showError(errorMessage);
      throw err;
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedUser(null);
  };

  if (!canReadUsers) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">You do not have access to this page.</Alert>
      </Container>
    );
  }

  if (isInitialLoad) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box />
          {canCreateUser && (
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SyncIcon />}
                onClick={handleSyncUsers}
                disabled={isSyncingUsers}
                sx={{
                  backgroundColor: "hsl(19 101% 51% / 1)",
                  "&:hover": {
                    backgroundColor: "hsl(19 101% 41% / 1)",
                  },
                }}
              >
                Sync User
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                sx={{
                  backgroundColor: "hsl(19 101% 51% / 1)",
                  "&:hover": {
                    backgroundColor: "hsl(19 101% 41% / 1)",
                  },
                }}
              >
                Add User
              </Button>
            </Box>
          )}
        </Box>

        <UserTable users={users} onEdit={handleEdit} onDelete={handleDelete} loading={loading} />

        <ConfirmDialog
          open={isDeleteDialogOpen}
          title="Delete user"
          message={
            userPendingDelete
              ? `Are you sure you want to delete ${userPendingDelete.displayName}? This action cannot be undone.`
              : "Are you sure you want to delete this user?"
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          isDangerous
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isLoading={isDeletingUser}
        />

        <UserFormDialog
          open={isDialogOpen}
          user={selectedUser}
          onClose={handleCloseDialog}
          onSave={handleSave}
          mode={dialogMode}
          availableUsers={availableUsersToAdd}
          currentTenantId={isActingAsGlobalAdmin ? null : selectedTenantId}
          isActingAsGlobalAdmin={isActingAsGlobalAdmin}
          isLoggedInGlobalAdmin={isGlobalAdmin}
          currentUserId={userSession?.user.id ?? null}
        />
      </Box>
    </Container>
  );
};

export default UserAccess;
